'use client'
import React from 'react'

import { useEffect, useRef, useCallback } from 'react'
import Editor, { type Monaco, type OnMount } from '@monaco-editor/react'
import type { editor } from 'monaco-editor'
import { useFileStore } from '@/lib/file-store'
import { Loader2 } from 'lucide-react'

const getLanguage = (fileName: string): string => {
  const ext = fileName.split('.').pop()?.toLowerCase()
  const languageMap: Record<string, string> = {
    js: 'javascript',
    jsx: 'javascript',
    ts: 'typescript',
    tsx: 'typescript',
    json: 'json',
    css: 'css',
    scss: 'scss',
    sass: 'scss',
    html: 'html',
    htm: 'html',
    md: 'markdown',
    mdx: 'markdown',
    xml: 'xml',
    svg: 'xml',
    yaml: 'yaml',
    yml: 'yaml',
    py: 'python',
    rb: 'ruby',
    go: 'go',
    rs: 'rust',
    sh: 'shell',
    bash: 'shell',
    sql: 'sql',
    graphql: 'graphql',
    gql: 'graphql',
    vue: 'html',
    svelte: 'html',
  }
  return languageMap[ext || ''] || 'plaintext'
}

export function CodeEditor() {
  const { activeFile, getFileContent, updateFileContent } = useFileStore()
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null)
  const monacoRef = useRef<Monaco | null>(null)

  const content = activeFile ? getFileContent(activeFile) : ''
  const language = activeFile ? getLanguage(activeFile) : 'plaintext'

  const handleEditorDidMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor
    monacoRef.current = monaco

    // Configure Monaco theme
    monaco.editor.defineTheme('webstudio-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '6a9955' },
        { token: 'keyword', foreground: '569cd6' },
        { token: 'string', foreground: 'ce9178' },
        { token: 'number', foreground: 'b5cea8' },
        { token: 'type', foreground: '4ec9b0' },
        { token: 'function', foreground: 'dcdcaa' },
        { token: 'variable', foreground: '9cdcfe' },
      ],
      colors: {
        'editor.background': '#1a1a2e',
        'editor.foreground': '#e0e0e0',
        'editorLineNumber.foreground': '#6b7280',
        'editorLineNumber.activeForeground': '#e0e0e0',
        'editor.selectionBackground': '#3d5a80',
        'editor.lineHighlightBackground': '#1f1f3a',
        'editorCursor.foreground': '#60a5fa',
        'editor.inactiveSelectionBackground': '#2d3748',
        'editorIndentGuide.background': '#2d2d4a',
        'editorIndentGuide.activeBackground': '#4a4a6a',
      },
    })
    monaco.editor.setTheme('webstudio-dark')

    // Configure TypeScript/JavaScript
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ESNext,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      jsx: monaco.languages.typescript.JsxEmit.React,
      allowSyntheticDefaultImports: true,
      esModuleInterop: true,
    })

    monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ESNext,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      jsx: monaco.languages.typescript.JsxEmit.React,
      allowSyntheticDefaultImports: true,
    })
  }, [])

  const handleChange = useCallback(
    (value: string | undefined) => {
      if (activeFile && value !== undefined) {
        updateFileContent(activeFile, value)
      }
    },
    [activeFile, updateFileContent]
  )

  useEffect(() => {
    if (editorRef.current && monacoRef.current) {
      monacoRef.current.editor.setTheme('webstudio-dark')
    }
  }, [])

  if (!activeFile) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[color:var(--editor-bg)] text-muted-foreground">
        <div className="text-center space-y-2">
          <p className="text-lg">Select a file to start editing</p>
          <p className="text-sm">or create a new file from the sidebar</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 h-full">
      <Editor
        height="100%"
        language={language}
        value={content}
        onChange={handleChange}
        onMount={handleEditorDidMount}
        theme="vs-dark"
        loading={
          <div className="flex items-center justify-center h-full bg-[color:var(--editor-bg)]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        }
        options={{
          minimap: { enabled: true, scale: 0.75 },
          fontSize: 14,
          fontFamily: 'var(--font-mono), monospace',
          lineHeight: 1.6,
          padding: { top: 16 },
          scrollBeyondLastLine: false,
          smoothScrolling: true,
          cursorBlinking: 'smooth',
          cursorSmoothCaretAnimation: 'on',
          renderWhitespace: 'selection',
          bracketPairColorization: { enabled: true },
          automaticLayout: true,
          tabSize: 2,
          wordWrap: 'on',
          folding: true,
          foldingHighlight: true,
          showFoldingControls: 'mouseover',
          guides: {
            indentation: true,
            bracketPairs: true,
          },
        }}
      />
    </div>
  )
}
